import React, { Fragment } from 'react';
import { isImmutable, Map } from 'immutable';
import classNames from 'classnames';
import { Icon } from '../Icon';

export const hasErrors = props => props.touched && props.errors.size > 0;

/**
 * Parses renderAttributes and returns a map of render options used to render
 * the correct field styles.
 *
 * @param {Object} props The props passed to the field
 * @param {Object} overrides A map of return values that can be overridden to
 *      force a particular result; valid properties are any of the properties of
 *      the return object
 * @param {Object} defaults Defaults to use for renderAttributes that aren't
 *      defined
 * @param {boolean} defaults.floating Should the field have a floating label
 * @param {boolean} defaults.clear Should the field have a clear button
 * @param {boolean} defaults.size What size should the field be
 * @param {boolean} defaults.wrapperClassName Class to add to the wrapper; this
 *      class will be added in addition to any defined by renderAttributes
 * @param {boolean} defaults.fieldClassName Class to add to the field; this
 *      class will be added in addition to any defined by renderAttributes
 *
 * @returns {Object} An object of properties used for determining how to render
 *        various parts of the field
 *  - hasLabel {boolean} Is there a label and should it be visible
 *  - placeholder {string} Placeholder value to show in the field
 *  - isFloating {boolean} Should the field have a floating label
 *  - hasClear {boolean} Should the field show a clear button
 *  - isRequired {boolean} Is the field required
 *  - hasLabelHelpText {boolean} Is there help text and should it be shown in
 *        the label
 *  - hasInlineHelpText {boolean} Is there help text and should it be shown
 *        below the field
 *  - disabledMessage {string} The disabled message text to show when the field
 *        is disabled instead of an empty field
 *  - hasEmptyOptions {boolean} Does the field have an options list defined and
 *        is the list empty
 *  - EmptyOptionsPlaceholder {Component} Component to render when
 *        hasEmptyOptions is true instead of an empty field
 *  - hasErrors {boolean} Is the field in an invalid state
 *  - size {string} Size of the file inputs
 *  - wrapperClassName {string} Class to add to the field wrapper element
 *  - fieldClassName {string} Class to add to the field element
 */
export const parseRenderOptions = (props, overrides, defaults = {}) => {
  // Parse renderAttributes
  const {
    omitWrapper,
    omitLabel: omitLabelByAttribute,
    required: requiredByAttribute,
    inlineHelpText,
    floating = defaults.floating,
    clear = defaults.clear,
    size = defaults.size,
    disabledMessage,
    EmptyOptionsPlaceholder,
    wrapperClassName,
    fieldClassName,
    ...custom
  } = isImmutable(props.renderAttributes)
    ? props.renderAttributes.toObject()
    : typeof props.renderAttributes === 'object'
      ? props.renderAttributes
      : {};

  // Allow overriding of render options
  const overridesMap = Map(overrides || {});

  // Define the options that get used by other options below, including checking
  // for their overrides
  const hasLabel = overridesMap.get(
    'hasLabel',
    !props.omitLabel && !omitLabelByAttribute && props.label,
  );
  const isFloating = overridesMap.get('isFloating', !!floating && hasLabel);
  const hasHelpText = overridesMap.get('hasHelpText', !!props.helpText);

  return {
    hasLabel,
    // If isFloating, a placeholder is required for proper styling
    placeholder:
      props.placeholder || (isFloating ? props.label || ' ' : undefined),
    isFloating,
    hasClear: !!clear && !!props.onChange,
    isRequired: props.required || !!requiredByAttribute,
    // Help text in label is only valid if there is a label and it sin't floating
    hasLabelHelpText: hasHelpText && hasLabel && !isFloating && !inlineHelpText,
    hasInlineHelpText:
      hasHelpText && (!!inlineHelpText || isFloating || !hasLabel),
    disabledMessage: !props.enabled && disabledMessage,
    hasEmptyOptions:
      isImmutable(props.options) &&
      props.options.isEmpty() &&
      !!EmptyOptionsPlaceholder,
    EmptyOptionsPlaceholder,
    hasErrors: hasErrors(props),
    size,
    omitWrapper,
    wrapperClassName: [wrapperClassName, defaults.wrapperClassName]
      .filter(Boolean)
      .join(' '),
    fieldClassName: [fieldClassName, defaults.fieldClassName]
      .filter(Boolean)
      .join(' '),
    // Add all overrides
    ...(overrides || {}),
    // Add custom object with other renderAttributes
    custom,
  };
};

/**
 * Generates the parts necessary to wrap a field so that a clear button can be
 * added. If `hasClear` is false, simply returns a Fragment. Otherwise, returns
 * a div with appropriate classes and a button to clear the value.
 *
 * This helper does not need to be used for fields where we don't want a clear
 * button (checkbox, radio, etc), or always have a clear button because it's
 * part of the default field implementation (typeahead fields).
 *
 * @params {Object} renderOptions The result of the parseRenderOptions function
 *      for the field
 * @params {Object} props The props passed to the field
 *
 * @return {Object} An object of properties used to render a wrapper so a
 *      floating button can be shown in the field
 *  - Tag {string} The tag to use for the wrapper element
 *  - props {Object} Props to pass to the wrapper element
 *  - content {Node} Content to render inside the wrapper after the field, which
 *      will render the clear button when appropriate
 */
export const getClearButtonWrapper = (renderOptions, props) =>
  renderOptions.hasClear && props.enabled
    ? {
        // If field is floating, we don't need an extra wrapper to float the
        // button because the field wrapper already supports floating elements
        Tag: !renderOptions.isFloating ? 'div' : Fragment,
        props: !renderOptions.isFloating ? { className: 'field-floating' } : {},
        // Render the clear button and set the value to an empty string onClick
        content: props.value && (
          <button
            type="button"
            className={classNames('btn btn-link-primary btn-floating', {
              'btn-sm': renderOptions.size === 'sm',
            })}
            onClick={() => props.onChange('')}
            aria-label="Clear Value"
            title="Clear Value"
          >
            <Icon name="x" md />
          </button>
        ),
      }
    : // If no clear buttons or field is disabled, return Fragment so nothing is
      // added to the dom
      { Tag: Fragment, props: {} };
